import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import DOMPurify from "isomorphic-dompurify";
import { useRealtimeInvalidate } from "@/hooks/useRealtimeInvalidate";
import {
  listInboxThreads,
  getInboxThread,
  markThreadRead,
  toggleThreadFlag,
  deleteThread,
  sendInboxReply,
  uploadOutboxAttachment,
} from "@/lib/inbox.functions";

export const Route = createFileRoute("/admin/inbox")({
  component: InboxPage,
});

type Filter = "all" | "unread" | "starred" | "archived";

function fmtDate(d: string | null | undefined) {
  if (!d) return "";
  const dt = new Date(d);
  const now = new Date();
  const sameDay = dt.toDateString() === now.toDateString();
  return sameDay
    ? dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : dt.toLocaleDateString([], { month: "short", day: "numeric" });
}

function InboxPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const threadsQ = useQuery({
    queryKey: ["admin", "inbox", "threads", filter, search],
    queryFn: () => listInboxThreads({ data: { filter, search: search || undefined } }),
    refetchInterval: 30000,
  });

  useRealtimeInvalidate([
    { table: "inbox_threads", queryKey: ["admin", "inbox", "threads"] },
    { table: "inbox_messages", queryKey: ["admin", "inbox"] },
  ]);

  // Auto-select first thread when list loads
  useEffect(() => {
    if (!selectedId && threadsQ.data && threadsQ.data.length > 0) {
      setSelectedId(threadsQ.data[0].id);
    }
  }, [threadsQ.data, selectedId]);

  return (
    <div>
      <div className="mb-6">
        <h1 className="headline-display text-3xl">Inbox</h1>
        <p className="text-sm text-muted-foreground mt-1">
          All mail sent to <code>@mapobserver.com</code> — sent and received via Resend.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[340px_1fr] gap-0 border border-border rounded-md overflow-hidden bg-background min-h-[70vh]">
        {/* Left: threads list */}
        <div className="border-r border-border flex flex-col">
          <div className="p-3 border-b border-border space-y-2">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search subject…"
              className="w-full text-sm border border-input px-2 py-1.5 rounded-md bg-transparent"
            />
            <div className="flex gap-1 text-xs">
              {(["all", "unread", "starred", "archived"] as Filter[]).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-2 py-1 rounded ${filter === f ? "bg-foreground text-background" : "hover:bg-paper-soft text-muted-foreground"}`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-y-auto flex-1 max-h-[70vh]">
            {threadsQ.isLoading ? (
              <p className="p-4 text-muted-foreground text-sm">Loading…</p>
            ) : !threadsQ.data || threadsQ.data.length === 0 ? (
              <p className="p-8 text-center text-muted-foreground text-sm">No messages.</p>
            ) : (
              threadsQ.data.map((t) => {
                const unread = (t.unread_count ?? 0) > 0;
                const active = t.id === selectedId;
                return (
                  <button
                    key={t.id}
                    onClick={() => setSelectedId(t.id)}
                    className={`w-full text-left px-3 py-3 border-b border-border block ${active ? "bg-paper-soft" : "hover:bg-paper-soft/50"}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs ${unread ? "font-semibold text-foreground" : "text-muted-foreground"}`}>
                        {truncateParticipants(t.participants as any, t.to_address)}
                      </span>
                      {t.starred ? <span title="Starred">★</span> : null}
                      {t.has_attachments ? <span title="Attachments">📎</span> : null}
                      <span className="ml-auto text-[11px] text-muted-foreground">{fmtDate(t.last_message_at)}</span>
                    </div>
                    <div className={`text-sm truncate ${unread ? "font-semibold" : ""}`}>
                      {t.subject || "(no subject)"}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{t.snippet}</div>
                    <div className="text-[10px] text-muted-foreground mt-1">
                      to {t.to_address ?? "—"} · {t.message_count} msg
                      {unread ? ` · ${t.unread_count} unread` : ""}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Right: selected thread */}
        <div className="min-h-[70vh]">
          {selectedId ? (
            <ThreadView threadId={selectedId} onDeleted={() => setSelectedId(null)} />
          ) : (
            <div className="p-12 text-center text-muted-foreground">Select a thread</div>
          )}
        </div>
      </div>
    </div>
  );

  function truncateParticipants(participants: string[] | null, toAddr: string | null | undefined) {
    const others = (participants ?? []).filter(
      (p) => !p.toLowerCase().endsWith("@mapobserver.com"),
    );
    const list = others.length ? others : (participants ?? []);
    if (list.length === 0) return toAddr ?? "—";
    return list.slice(0, 2).join(", ") + (list.length > 2 ? ` +${list.length - 2}` : "");
  }
}

function ThreadView({ threadId, onDeleted }: { threadId: string; onDeleted: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["admin", "inbox", "thread", threadId],
    queryFn: () => getInboxThread({ data: { thread_id: threadId } }),
  });

  const markRead = useMutation({
    mutationFn: () => markThreadRead({ data: { thread_id: threadId } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "inbox"] });
    },
  });

  useEffect(() => {
    if (q.data?.thread && (q.data.thread.unread_count ?? 0) > 0) {
      markRead.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data?.thread?.id]);

  const toggleStar = useMutation({
    mutationFn: (starred: boolean) =>
      toggleThreadFlag({ data: { thread_id: threadId, starred } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "inbox"] }),
  });
  const setStatus = useMutation({
    mutationFn: (status: "open" | "archived") =>
      toggleThreadFlag({ data: { thread_id: threadId, status } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "inbox"] }),
  });
  const del = useMutation({
    mutationFn: () => deleteThread({ data: { thread_id: threadId } }),
    onSuccess: () => {
      toast.success("Thread deleted");
      qc.invalidateQueries({ queryKey: ["admin", "inbox"] });
      onDeleted();
    },
  });

  if (q.isLoading) return <div className="p-6 text-muted-foreground">Loading…</div>;
  if (q.error || !q.data) return <div className="p-6 text-destructive">Failed to load thread.</div>;

  const { thread, messages, attachments, signedUrls } = q.data;

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-border flex items-start gap-3">
        <div className="flex-1">
          <h2 className="text-lg font-semibold">{thread.subject || "(no subject)"}</h2>
          <p className="text-xs text-muted-foreground mt-1">
            {messages.length} message{messages.length === 1 ? "" : "s"} · to {thread.to_address ?? "—"}
          </p>
        </div>
        <button
          onClick={() => toggleStar.mutate(!thread.starred)}
          className="text-lg"
          title={thread.starred ? "Unstar" : "Star"}
        >
          {thread.starred ? "★" : "☆"}
        </button>
        <button
          onClick={() =>
            setStatus.mutate(thread.status === "archived" ? "open" : "archived")
          }
          className="text-xs border border-border px-2 py-1 rounded hover:bg-paper-soft"
        >
          {thread.status === "archived" ? "Unarchive" : "Archive"}
        </button>
        <button
          onClick={() => {
            if (confirm("Delete this thread and all messages?")) del.mutate();
          }}
          className="text-xs border border-border px-2 py-1 rounded hover:bg-destructive/10 text-destructive"
        >
          Delete
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[50vh]">
        {messages.map((m: any) => {
          const atts: any[] = attachments[m.id] ?? [];
          const outbound = m.direction === "outbound";
          return (
            <div
              key={m.id}
              className={`border border-border rounded-md p-3 ${outbound ? "bg-paper-soft ml-8" : "mr-8"}`}
            >
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                <span className={outbound ? "text-primary" : ""}>
                  {outbound ? "Sent" : "Received"}
                </span>
                <span>·</span>
                <span>
                  <b className="text-foreground">
                    {m.from_name ? `${m.from_name} <${m.from_address}>` : m.from_address}
                  </b>
                </span>
                <span>·</span>
                <span>to {(m.to_addresses ?? []).join(", ")}</span>
                <span className="ml-auto">{fmtDate(m.sent_at ?? m.received_at ?? m.created_at)}</span>
              </div>
              <MessageBody html={m.html_body} text={m.text_body} />
              {atts.length > 0 ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {atts.map((a: any) => (
                    <a
                      key={a.id}
                      href={signedUrls[a.id] ?? "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs border border-border px-2 py-1 rounded hover:bg-paper-soft"
                    >
                      📎 {a.filename}
                      {a.size_bytes ? ` (${Math.round(a.size_bytes / 1024)} KB)` : ""}
                    </a>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <ReplyComposer thread={thread} messages={messages} />
    </div>
  );
}

function MessageBody({ html, text }: { html: string | null; text: string | null }) {
  const clean = useMemo(() => {
    if (html) {
      return DOMPurify.sanitize(html, {
        ALLOWED_TAGS: [
          "a", "b", "i", "u", "em", "strong", "p", "br", "div", "span", "ul", "ol", "li",
          "blockquote", "pre", "code", "h1", "h2", "h3", "h4", "img", "hr", "table", "thead",
          "tbody", "tr", "td", "th",
        ],
        ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "title", "style"],
        FORBID_TAGS: ["script", "style", "iframe", "object", "embed"],
      });
    }
    return null;
  }, [html]);
  if (clean) {
    return (
      <div
        className="prose prose-sm max-w-none break-words"
        dangerouslySetInnerHTML={{ __html: clean }}
      />
    );
  }
  return <pre className="whitespace-pre-wrap text-sm font-sans">{text ?? "(empty)"}</pre>;
}

function ReplyComposer({ thread, messages }: { thread: any; messages: any[] }) {
  const qc = useQueryClient();
  const lastInbound = [...messages].reverse().find((m) => m.direction === "inbound");
  const defaultTo = lastInbound?.from_address ?? "";
  const defaultCc: string[] = lastInbound?.cc_addresses ?? [];
  const defaultFrom =
    thread.to_address ??
    lastInbound?.to_addresses?.find((a: string) => a?.toLowerCase().endsWith("@mapobserver.com")) ??
    "hello@mapobserver.com";
  const parentMessageId: string | null = lastInbound?.message_id ?? null;
  const parentRefs: string[] = lastInbound?.reference_ids ?? [];
  const subject = thread.subject?.match(/^re:\s*/i) ? thread.subject : `Re: ${thread.subject || ""}`.trim();

  const [to, setTo] = useState(defaultTo);
  const [cc, setCc] = useState(defaultCc.join(", "));
  const [from, setFrom] = useState(defaultFrom);
  const [body, setBody] = useState("");
  const [subj, setSubj] = useState(subject);
  const [atts, setAtts] = useState<{ path: string; filename: string; size: number }[]>([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setTo(defaultTo);
    setCc(defaultCc.join(", "));
    setFrom(defaultFrom);
    setSubj(subject);
    setBody("");
    setAtts([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread.id]);

  const send = useMutation({
    mutationFn: () =>
      sendInboxReply({
        data: {
          thread_id: thread.id,
          to: to.split(",").map((s: string) => s.trim()).filter(Boolean),
          cc: cc.split(",").map((s: string) => s.trim()).filter(Boolean),
          from_address: from,
          subject: subj,
          body_text: body,
          attachment_paths: atts.map((a) => a.path),
          in_reply_to: parentMessageId,
          reference_ids: parentMessageId
            ? [...parentRefs, parentMessageId].slice(-20)
            : parentRefs,
        },
      }),
    onSuccess: () => {
      toast.success("Reply sent");
      setBody("");
      setAtts([]);
      qc.invalidateQueries({ queryKey: ["admin", "inbox"] });
    },
    onError: (err: any) => toast.error(err?.message ?? "Send failed"),
  });

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Max 25MB per file");
      return;
    }
    setUploading(true);
    try {
      const buf = new Uint8Array(await file.arrayBuffer());
      let bin = "";
      for (let i = 0; i < buf.length; i++) bin += String.fromCharCode(buf[i]);
      const res = await uploadOutboxAttachment({
        data: {
          filename: file.name,
          content_base64: btoa(bin),
          content_type: file.type || "application/octet-stream",
        },
      });
      setAtts((a) => [...a, res]);
    } catch (err: any) {
      toast.error(err?.message ?? "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  return (
    <div className="border-t border-border p-3 space-y-2 bg-paper-soft/40">
      <div className="grid grid-cols-[70px_1fr] gap-2 text-sm items-center">
        <label className="text-muted-foreground">From</label>
        <input
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className="border border-input px-2 py-1 rounded bg-background text-sm"
        />
        <label className="text-muted-foreground">To</label>
        <input
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="comma,separated@example.com"
          className="border border-input px-2 py-1 rounded bg-background text-sm"
        />
        <label className="text-muted-foreground">Cc</label>
        <input
          value={cc}
          onChange={(e) => setCc(e.target.value)}
          className="border border-input px-2 py-1 rounded bg-background text-sm"
        />
        <label className="text-muted-foreground">Subject</label>
        <input
          value={subj}
          onChange={(e) => setSubj(e.target.value)}
          className="border border-input px-2 py-1 rounded bg-background text-sm"
        />
      </div>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Write your reply…"
        rows={8}
        className="w-full border border-input px-2 py-2 rounded bg-background text-sm font-sans"
      />
      {atts.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {atts.map((a) => (
            <span key={a.path} className="text-xs border border-border px-2 py-1 rounded flex items-center gap-2">
              📎 {a.filename} ({Math.round(a.size / 1024)} KB)
              <button
                onClick={() => setAtts((prev) => prev.filter((x) => x.path !== a.path))}
                className="text-muted-foreground hover:text-destructive"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-2">
        <label className="text-xs text-muted-foreground cursor-pointer border border-border px-2 py-1 rounded hover:bg-background">
          {uploading ? "Uploading…" : "Attach file"}
          <input type="file" onChange={handleFile} className="hidden" disabled={uploading} />
        </label>
        <button
          onClick={() => send.mutate()}
          disabled={send.isPending || !body.trim() || !to.trim()}
          className="bg-foreground text-background text-sm px-4 py-1.5 rounded disabled:opacity-50"
        >
          {send.isPending ? "Sending…" : "Send reply"}
        </button>
      </div>
    </div>
  );
}
